/**
 * Card stacks — Shift and Boost piles (SPEC.md §4.1's third hardest mechanic; CR §8.10, §8.3).
 *
 * A pile is **one board object with one card in play**. Its top card is an ordinary card in
 * the Play zone: it quests, it challenges, it takes damage, its characteristics are read, its
 * abilities function. The cards under it are **not in play** (CR §8.10.4) — they are in the
 * `under` zone, so every "is this in play?" test in the engine (`zone === 'play'`) excludes
 * them without being taught anything: statics and their scopes (`modifiers.ts`), selector
 * pools and target pools (`abilities/selectors.ts`), the game state check's banish sweep
 * (`rules.ts`), activation (`abilities/interpret.ts`) and the replacement scan
 * (`replacements.ts`) all read the play-zone arrays or that field.
 *
 * ```
 * player.play    [ … 7 … ]        7 is in play; its states govern the whole pile
 * state.stacks   { top: 7, under: [3, 5], shifted: [7] }
 * player.under   [ 3, 5 ]         3 and 5 are in no player's play zone
 * ```
 *
 * The two redundant halves — the pile record and the `under` zone array — are the same
 * redundancy `CardInstance.zone` already has with the zone arrays, and `checkInvariants`
 * cross-checks them the same way. Keeping the under-cards in a real zone array is what makes
 * `moveInstance` the only code that moves a card, in both directions, including the pile's.
 *
 * ## What the pile does as a unit
 *
 * - **It moves zones together.** When the top card leaves play, every card under it goes to
 *   its owner's discard (CR §8.10.4, §1.9) — banishing a stack discards the whole pile, and
 *   bouncing the top card discards the rest. That is a hook inside `moveInstance`.
 * - **It inherits, it does not reset.** A Shift card enters with the base's exerted, drying
 *   and damage states, and the effects that were applying to the base keep applying to the
 *   character it now is (rules inventory §5: "keeps effects applying to the shifted-onto
 *   character"). The shifting card keeps its own instance id, so records already scoped to
 *   *it* survive by construction; the base's records are re-scoped onto it here, and anything
 *   still naming the base is dropped by `moveInstance` exactly as leaving play does.
 * - **Its top card can change.** Temporary Shift returns *only its own card* to hand at the
 *   end of the turn — from the middle of a pile if something was shifted on top of it — and
 *   the card beneath is restored as the top, carrying the pile's states.
 *
 * ## Face-down under-cards
 *
 * Boost puts the top card of a deck **facedown** under a card, and a facedown card in play is
 * private information — the one CR exception to the public Play zone (§7.4.2.1); nobody may
 * look at it. That is recorded by clearing the card's `knownTo` outright, which is the one
 * place in the engine where knowledge is *not* monotone, and it is deliberate: `playerView`
 * and `determinize` then redact and permute the card with no special case at all, and the
 * identity becomes public again the moment the pile reaches the discard.
 */
import { shiftSpecOf } from './costs.ts';
import type { InstanceId, PlayerIndex } from './ids.ts';
import { retargetModifiers } from './modifiers.ts';
import type { CardInstance, GameState } from './state.ts';
import { instanceAt, playerAt, zoneOf } from './state.ts';
import { emit, moveInstance } from './zones.ts';

/**
 * One pile of cards on the board.
 *
 * Plain data, like everything else on `GameState`: three arrays of instance ids, no identity
 * and nothing derived. A stack exists only while something is under something else — a lone
 * card in play has no record, and the record is dropped as soon as its last under-card goes.
 */
export interface CardStack {
  /** The card in play. Its states govern the pile, and the pile moves with it. */
  top: InstanceId;
  /** The cards under it, nearest the top card first. Every one is in the `under` zone. */
  under: InstanceId[];
  /**
   * The cards of this pile — top or under — that arrived on it by **Shift**. It is what
   * `Temporary Shift`'s end-of-turn return reads: a card played for its ink cost never
   * returns, and a temporary card that something was later shifted on top of still does
   * (CR 2.2 §8.10.8, "works even from the middle of a stack").
   */
  shifted: InstanceId[];
}

// ---------------------------------------------------------------------------
// Reading a stack
// ---------------------------------------------------------------------------

/** The pile this card is the top of, if it has one under it. */
export function stackToppedBy(state: GameState, top: InstanceId): CardStack | undefined {
  return state.stacks.find((stack) => stack.top === top);
}

/** The pile this card belongs to, whether it is the top card or under it. */
export function stackOf(state: GameState, id: InstanceId): CardStack | undefined {
  return state.stacks.find((stack) => stack.top === id || stack.under.includes(id));
}

/** The cards under this one, nearest first — empty for the vast majority of cards. */
export function cardsUnder(state: GameState, id: InstanceId): readonly InstanceId[] {
  return stackToppedBy(state, id)?.under ?? [];
}

/** How many cards are under this one (CR §8.3's "for each card under this character"). */
export function underCount(state: GameState, id: InstanceId): number {
  return cardsUnder(state, id).length;
}

/** Whether this card is under another one — the CR's "not in play" (§8.10.4). */
export function isUnderCard(state: GameState, id: InstanceId): boolean {
  return instanceAt(state, id).zone === 'under';
}

/** The card in play this one is part of: itself, or the top of the pile it is under. */
export function topCardOf(state: GameState, id: InstanceId): InstanceId {
  const stack = stackOf(state, id);
  return stack === undefined ? id : stack.top;
}

// ---------------------------------------------------------------------------
// Building a stack
// ---------------------------------------------------------------------------

/** The pile record for this top card, created if it does not exist yet. */
function stackFor(state: GameState, top: InstanceId): CardStack {
  const existing = stackToppedBy(state, top);
  if (existing !== undefined) return existing;
  const stack: CardStack = { top, under: [], shifted: [] };
  state.stacks.push(stack);
  return stack;
}

/** Forget a pile record. The cards themselves are the caller's business. */
function forgetStack(state: GameState, stack: CardStack): void {
  state.stacks = state.stacks.filter((each) => each !== stack);
}

/**
 * Put one card into the `under` zone of the pile's controller.
 *
 * `moveInstance` sends a card to its **owner** (CR §7.1.3), which is right for every other
 * zone and wrong for this one: a card under someone else's card is part of *their* board
 * object, so the zone array has to follow the pile.
 */
function placeUnder(state: GameState, id: InstanceId, controller: PlayerIndex): void {
  moveInstance(state, id, 'under');
  const card = instanceAt(state, id);
  if (card.controller === controller) return;
  const from = zoneOf(playerAt(state, card.controller), 'under');
  const at = from.indexOf(id);
  if (at !== -1) from.splice(at, 1);
  card.controller = controller;
  zoneOf(playerAt(state, controller), 'under').push(id);
}

/**
 * Play a card on top of its Shift bases (CR §8.10) — everything about a Shift play except
 * paying for it, which `payments.ts` did before the card moved.
 *
 * The card is already in play (`rules.ts` moved it there and gave it a newcomer's states);
 * this is what makes it a stack instead:
 *
 * 1. **the states are inherited, not reset** — exerted, drying and damage come off the bases,
 *    which is what lets a shifted character quest the turn it arrives. `Duo`/`Combo Shift`
 *    take the **worst** of two bases (CR 2.2 §8.10.8: drying beats dry, exerted beats ready)
 *    and *all* of their damage: the counters were on the cards and the cards are still here,
 *    so they end up on the card that now represents them;
 * 2. **the effects on the base keep applying to the character** — its applied statics are
 *    re-scoped onto the shifting card before it leaves play, so a "+2 {S} this turn" on the
 *    base is a "+2 {S} this turn" on the stack;
 * 3. **a base that was itself a stack keeps its pile**, which moves under the new top card in
 *    place. Its record is dropped *before* the base moves, because dropping a pile's record is
 *    exactly what stops `moveInstance` from discarding the pile.
 */
export function shiftOnto(
  state: GameState,
  card: CardInstance,
  onto: readonly InstanceId[],
): void {
  let exerted = false;
  let drying = false;
  let damage = 0;
  const pile: InstanceId[] = [];
  const shifted: InstanceId[] = [];

  for (const id of onto) {
    const base = instanceAt(state, id);
    // Read the base's states before `moveInstance` clears them (CR §1.9).
    exerted = exerted || base.exerted;
    drying = drying || base.drying;
    damage += base.damage;
    const existing = stackToppedBy(state, id);
    pile.push(id, ...(existing?.under ?? []));
    if (existing !== undefined) {
      shifted.push(...existing.shifted);
      forgetStack(state, existing);
    }
    retargetModifiers(state, id, card.id);
  }

  for (const id of pile) {
    // The cards that were already under a base are already in the `under` zone; only the
    // bases themselves are leaving play.
    if (instanceAt(state, id).zone === 'play') placeUnder(state, id, card.controller);
    // The per-turn fact "put a card under this character" is written exactly where the event
    // is emitted, so the two can never disagree; the Ready step clears it.
    state.cardsPutUnderThisTurn.push(card.id);
    emit(state, {
      type: 'card-put-under',
      player: card.controller,
      instance: id,
      defId: instanceAt(state, id).defId,
      top: card.id,
    });
  }

  card.exerted = exerted;
  card.drying = drying;
  card.damage = damage;

  const stack = stackFor(state, card.id);
  stack.under = pile;
  stack.shifted = [...shifted.filter((id) => pile.includes(id)), card.id];
}

/**
 * The general put-under verb: move a **named** card into the pile topped by `top`, wherever
 * the card is now. `options.facedown` clears its identity the way Boost's does (CR §7.4.2.1);
 * left unset, the card stays exactly as known as it was — the shape ticket 63's "faceup" put
 * needs, since a card leaving a public discard is already known to everyone. `top` must be in
 * play; a `top` that has left does nothing (CR §1.2.3).
 */
export function putCardUnder(
  state: GameState,
  id: InstanceId,
  top: InstanceId,
  options: { facedown?: boolean } = {},
): boolean {
  const card = instanceAt(state, top);
  if (card.zone !== 'play') return false;
  placeUnder(state, id, card.controller);
  if (options.facedown === true) {
    // "Facedown cards in play are unviewable by anyone" (CR §7.4.2.1) — including their owner.
    // The identity is public again once the pile reaches the discard.
    instanceAt(state, id).knownTo = 0;
  }
  // The bottom of the pile: the cards directly under the top card are its Shift bases, and
  // they are what a Temporary Shift extraction promotes.
  stackFor(state, top).under.push(id);
  // The per-turn fact "put a card under this character" is written exactly where the event is
  // emitted, so the two can never disagree; the Ready step clears it.
  state.cardsPutUnderThisTurn.push(top);
  emit(state, {
    type: 'card-put-under',
    player: card.controller,
    instance: id,
    defId: instanceAt(state, id).defId,
    top,
  });
  return true;
}

/**
 * Boost (CR §8.3): put the top card of this card's controller's deck **facedown** under it.
 *
 * Putting a card under is not playing it and not drawing it, so nothing is emitted about the
 * card's identity and nobody may look at it. An empty deck does as much as it can, which is
 * nothing (CR §1.2.3).
 */
export function boostUnder(state: GameState, top: InstanceId): InstanceId | null {
  const card = instanceAt(state, top);
  if (card.zone !== 'play') return null;
  const id = playerAt(state, card.controller).deck[0];
  if (id === undefined) return null;
  return putCardUnder(state, id, top, { facedown: true }) ? id : null;
}

/**
 * Detach a card from the pile it is under, without moving it anywhere — the bookkeeping half
 * of "play a card from under another one" (ticket 63): `stacks.ts` is the only code that may
 * touch `state.stacks`, so `rules.ts`'s play machinery calls this before it moves a card in
 * the `under` zone into play, the same way `extractFromStack` does for a return to hand. A
 * no-op for a card that is not under anything.
 */
export function leaveUnderPile(state: GameState, id: InstanceId): void {
  const stack = stackOf(state, id);
  if (stack === undefined || stack.top === id) return;
  stack.under = stack.under.filter((each) => each !== id);
  if (stack.under.length === 0) forgetStack(state, stack);
}

// ---------------------------------------------------------------------------
// Taking a stack apart
// ---------------------------------------------------------------------------

/**
 * The top card of this pile is leaving play, so the pile does too: every card under it goes
 * to its owner's discard (CR §8.10.4 — a card under a card is not in play and has nowhere
 * else to be). Called by `moveInstance`, which is why banishing, bouncing and discarding a
 * stack all behave the same way without any of them knowing about stacks.
 */
export function dissolveStack(state: GameState, top: InstanceId): void {
  const stack = stackToppedBy(state, top);
  if (stack === undefined) return;
  forgetStack(state, stack);
  const player = instanceAt(state, top).controller;
  const cards = [...stack.under];
  for (const id of cards) moveInstance(state, id, 'discard');
  emit(state, { type: 'stack-dissolved', player, top, instances: cards });
}

/**
 * Take one card out of a pile and put it somewhere else — Temporary Shift's return
 * (CR 2.2 §8.10.8), including the "from the middle of a stack" case.
 *
 * Extracting the **top** card promotes the card beneath it back into play, carrying the
 * pile's states: the character stays the same board object, it just loses the card that was
 * representing it. Extracting an under-card changes nothing else about the pile.
 */
export function extractFromStack(state: GameState, id: InstanceId, to: 'hand' | 'discard'): void {
  const stack = stackOf(state, id);
  if (stack === undefined) {
    moveInstance(state, id, to);
    return;
  }
  stack.shifted = stack.shifted.filter((each) => each !== id);
  if (stack.top !== id) {
    stack.under = stack.under.filter((each) => each !== id);
    if (stack.under.length === 0) forgetStack(state, stack);
    moveInstance(state, id, to);
    return;
  }

  const top = instanceAt(state, id);
  // The states belong to the pile, not to the card that was on top of it.
  const exerted = top.exerted;
  const drying = top.drying;
  const damage = top.damage;
  const [promoted, ...rest] = stack.under;
  if (promoted === undefined) {
    forgetStack(state, stack);
    moveInstance(state, id, to);
    return;
  }
  // Re-point the record before the card moves: a pile whose top card is leaving is discarded
  // outright (`dissolveStack`), and this is not that — the pile stays, under a new top card.
  stack.top = promoted;
  stack.under = rest;
  if (rest.length === 0) forgetStack(state, stack);
  moveInstance(state, id, to);
  moveInstance(state, promoted, 'play');
  const card = instanceAt(state, promoted);
  card.exerted = exerted;
  card.drying = drying;
  card.damage = damage;
  emit(state, {
    type: 'card-left-stack',
    player: card.controller,
    instance: promoted,
    defId: card.defId,
    top: null,
  });
}

/**
 * `Temporary [.] Shift N` (CR 2.2 §8.10.8): "at the end of your turn, remove all damage from
 * this character and return this card to your hand". Run in the End-of-Turn phase by
 * `engine.ts`, for the active player's piles only.
 *
 * "This character" is the whole pile, so the damage comes off its top card whether or not the
 * returning card is the one on top; "this card" is only the temporary one, which is what
 * leaves. A card that has been banished is in no pile and so returns nothing, exactly as the
 * keyword says.
 */
export function returnTemporaryShifts(state: GameState, player: PlayerIndex): void {
  // Snapshot the work first: extracting one card re-points the pile it came from.
  const returning: InstanceId[] = [];
  for (const stack of state.stacks) {
    if (instanceAt(state, stack.top).controller !== player) continue;
    for (const id of [stack.top, ...stack.under]) {
      if (!stack.shifted.includes(id)) continue;
      if (shiftSpecOf(state, instanceAt(state, id))?.temporary !== true) continue;
      returning.push(id);
    }
  }
  for (const id of returning) {
    const stack = stackOf(state, id);
    if (stack === undefined) continue;
    instanceAt(state, stack.top).damage = 0;
    extractFromStack(state, id, 'hand');
    emit(state, {
      type: 'card-returned',
      player,
      instance: id,
      defId: instanceAt(state, id).defId,
      // The whole pile is in play (CR §8.10), whether this card was the visible top or one of
      // the ones stacked under it — `from: 'play'` regardless of which internal zone it sat in.
      from: 'play',
    });
  }
}
